import { useState } from 'react'

import {
  Header,
  IconNotification,
  InfoAlert,
  Space,
  InfoBadge,
  InfoBadgeTypes,
  Text,
} from '@procurify/ui'
import { Link } from 'react-router-dom'

import { VendorContactInformation } from '@webapp/containers/PaymentManagePayeesView/components/VendorContactInformation'
import { VendorRequestInformation } from '@webapp/containers/PaymentManagePayeesView/components/VendorRequestInformation'
import { VendorSettings } from '@webapp/containers/PaymentManagePayeesView/components/VendorSettings'
import { useAppRoutes, useProcurifyIntl } from '@webapp/hooks'
import { type Vendor } from '@webapp/models'
import { VendorTypes } from '@webapp/models/enums/Vendor'
import {
  PaymentManagePayeesPageTabs,
  PaymentManagePayeesPageVendorDetailsTabs,
} from '@webapp/pages'
import { type Enum, isPendingVendorApproval } from '@webapp/utils'
import messages from './messages'
import { TabPanes } from '../TabPanes'
import { VendorDetailStyled } from './styles/VendorDetailStyled'

export interface IVendorDetailProps {
  vendor: Vendor
  isVendorApprovalsEnabled?: boolean
}

const VendorDetailTabs = {
  CONTACT_INFO: '1',
  SETTINGS: '2',
  PAYMENT_METHODS: '3',
} as const
type VendorDetailTab = Enum<typeof VendorDetailTabs>

export const VendorDetail = ({
  vendor,
  isVendorApprovalsEnabled,
}: IVendorDetailProps) => {
  const intl = useProcurifyIntl()
  const { url } = useAppRoutes()

  const [activeTab, setActiveTab] = useState<VendorDetailTab>(
    VendorDetailTabs.CONTACT_INFO
  )

  const isPendingVendor =
    isVendorApprovalsEnabled && isPendingVendorApproval(vendor)

  return (
    <VendorDetailStyled>
      <Space align='stretch'>
        <Space direction='horizontal' align='center'>
          <Header as='h4'>{vendor?.name}</Header>
          {isPendingVendor && (
            <InfoBadge
              text={intl.formatMessage(messages.pendingRequestLabel)}
              type={InfoBadgeTypes.LOW_PRIORITY}
              size='small'
            />
          )}
          {!isPendingVendor && vendor.type === VendorTypes.PREFERRED && (
            <InfoBadge
              text={intl.formatMessage(messages.preferredLabel)}
              type={InfoBadgeTypes.LOW_PRIORITY}
              size='small'
            />
          )}
        </Space>
        {isPendingVendor ? (
          <VendorRequestInformation vendorRequest={vendor.vendor_request} />
        ) : (
          <TabPanes
            activeKey={activeTab}
            onChange={(tab) => setActiveTab(tab as VendorDetailTab)}
          >
            <TabPanes.Pane
              label={intl.formatMessage(messages.contactInformationTabLabel)}
              key={VendorDetailTabs.CONTACT_INFO}
            >
              <VendorContactInformation vendor={vendor} />
            </TabPanes.Pane>
            <TabPanes.Pane
              label={intl.formatMessage(messages.settingsTabLabel)}
              key={VendorDetailTabs.SETTINGS}
            >
              <VendorSettings vendor={vendor} />
            </TabPanes.Pane>
            <TabPanes.Pane
              label={intl.formatMessage(messages.paymentMethodsHeader)}
              key={VendorDetailTabs.PAYMENT_METHODS}
            >
              <InfoAlert icon={<IconNotification />}>
                <Text>
                  {intl.formatMessage(
                    messages.paymentMethodsRedirectAlertText,
                    {
                      a: (text) => (
                        <Link
                          to={url('PaymentManagePayeesPage', {
                            type: PaymentManagePayeesPageTabs.VENDORS,
                            id: vendor?.id.toString() || '',
                            tab: PaymentManagePayeesPageVendorDetailsTabs.PAYMENT_METHODS,
                          })}
                        >
                          {text}
                        </Link>
                      ),
                    }
                  )}
                </Text>
              </InfoAlert>
            </TabPanes.Pane>
          </TabPanes>
        )}
      </Space>
    </VendorDetailStyled>
  )
}

VendorDetail.displayName = 'VendorDetail'
